<script setup lang="ts">
import { computed, ref, nextTick, onUnmounted, onMounted } from 'vue';
import dashboardIntroImage from '@/assets/dashboard-intro.png';
import { GridLayout, GridItem } from 'grid-layout-plus';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import { cloneDeep, throttle } from 'lodash';
import { v4 as uuid } from 'uuid';
import ConnxWidgetWrapper from './ConnxWidgetWrapper.vue';
import { Widget, LayoutItem } from '@/interfaces/Dashboard';
import ConnxDashboardSidebar from './ConnxDashboardSidebar.vue';
import useDashboardStore, { PREVIEW_WIDGET_KEY, PENDING_WIDGET_PREFIX } from '@/stores/Dashboard/dashboard-store';

const DRAG_OFFSET_HEIGHT = 100;
const DRAG_OFFSET_WIDTH = 200;
const COLUMN_NUM = 12;
const ROW_HEIGHT = 115;

type MousePosition = { x: number; y: number };

const props = defineProps<{
  editing: boolean;
  loading: boolean;
  mousePosition: MousePosition;
  widgetSettingsId: string | null;
}>();

const dashboardStore = useDashboardStore();
const gridLayout = ref<InstanceType<typeof GridLayout> | null>(null);
const wrapper = ref<HTMLElement | null>(null);
const selectedWidget = ref<Widget | null>(null);
const optionsOpen = ref<string>('');

const previewVisible = ref(false);
const isDragging = ref(false);
const isComponentMounted = ref(false);

const widgetRefs = ref(new Map<string, InstanceType<typeof ConnxWidgetWrapper>>());

// Add lifecycle management
onMounted(() => {
  isComponentMounted.value = true;
});

onUnmounted(() => {
  isComponentMounted.value = false;
  // Clean up widget refs to prevent memory leaks
  widgetRefs.value.clear();
});

const toggleOptionsOpen = (i: string, open: boolean) => {
  if (!isComponentMounted.value) return;
  optionsOpen.value = open ? i : '';
};

const onWidgetMoved = (i: string) => {
  if (!isComponentMounted.value) return;
  scrollToWidget(i);
};

const scrollToWidget = (itemId: string, offset = 40) => {
  if (!isComponentMounted.value) return;

  setTimeout(() => {
    const comp = widgetRefs.value.get(itemId);
    if (!comp) return;

    const el = comp.$el || comp;
    if (!el || !el.getBoundingClientRect) return;

    const rect = el.getBoundingClientRect();
    const isVisible =
      rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.bottom > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
      rect.right > 0;

    if (isVisible) return;

    const elementTop = rect.top + window.pageYOffset;
    const targetScroll = elementTop - offset;

    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });
  }, 150);
};

const colWidth = computed(() => {
  if (!wrapper.value) return 0;
  return wrapper.value.offsetWidth / 12;
});

const activeLayout = computed(() => dashboardStore.activeDashboardTabLayout);

const containerHeight = computed(() => {
  if (!isComponentMounted.value) return '100vh';

  if (activeLayout.value && activeLayout.value.length) {
    const maxRow = Math.max(...activeLayout.value.map((item) => item.y + item.h));
    const base = maxRow * ROW_HEIGHT * (props.editing ? 2 : 1);

    const fallback = window.innerHeight - 131;

    return `${Math.max(base, fallback)}px`;
  }

  return 'calc(100vh - 131px)';
});

const gridSettings = computed(() => ({
  isDraggable: props.editing,
  isResizable: props.editing,
  isMirrored: false,
  isBounded: false,
  transformScale: 1,
  preventCollision: false,
  autoSize: true,
  compact: true,
  verticalCompact: true,
  restoreOnDrag: true,
  colNum: COLUMN_NUM,
  rowHeight: ROW_HEIGHT,
  responsive: false,
  useCssTransforms: true,
  margin: [0, 0],
}));

const setSelectedWidget = (widget: Widget) => {
  if (!isComponentMounted.value) return;
  selectedWidget.value = cloneDeep(widget);
};

const setWidgetRef = (el: InstanceType<typeof ConnxWidgetWrapper> | null, id: string) => {
  if (!isComponentMounted.value) return;

  if (el) {
    widgetRefs.value.set(id, el);
  } else {
    widgetRefs.value.delete(id);
  }
};

const selectedWidgetDimensions = computed(() =>
  selectedWidget.value
    ? {
        w: selectedWidget.value.defaultJsonProperties.w,
        h: selectedWidget.value.defaultJsonProperties.h,
      }
    : { w: 0, h: 0 }
);

// Check if mouse is inside grid
const isMouseInGrid = computed(() => {
  if (!wrapper.value || !isComponentMounted.value) return false;
  const parentRect = wrapper.value.getBoundingClientRect();
  return (
    props.mousePosition.x > parentRect.left &&
    props.mousePosition.x < parentRect.right &&
    props.mousePosition.y > parentRect.top &&
    props.mousePosition.y < parentRect.bottom
  );
});

/**
 * Shared helper to calculate grid position from mouse coordinates
 */
const calcNewPosition = (item: any) => {
  if (!wrapper.value || !isComponentMounted.value) return { x: 0, y: 0 };
  const parentRect = wrapper.value.getBoundingClientRect();
  const top = props.mousePosition.y - parentRect.top - DRAG_OFFSET_HEIGHT;
  const left = props.mousePosition.x - parentRect.left - DRAG_OFFSET_WIDTH;
  return item.calcXY(top, left);
};

const onDragStart = () => {
  if (!isComponentMounted.value) return;
  isDragging.value = true;
  previewVisible.value = false;
};

const onDrag = throttle(() => {
  if (!wrapper.value || !gridLayout.value || !isDragging.value || !selectedWidget.value || !isComponentMounted.value)
    return;

  const mouseInGrid = isMouseInGrid.value;
  // Add preview item if needed
  if (mouseInGrid && !dashboardStore.hasPreviewWidget) {
    const gridPos = calculateGridPosition(
      props.mousePosition.x,
      props.mousePosition.y,
      selectedWidgetDimensions.value.w,
      selectedWidgetDimensions.value.h
    );
    dashboardStore.addDashboardTabWidget(
      {
        ...selectedWidget.value,
        defaultJsonProperties: {
          ...selectedWidget.value.defaultJsonProperties,
          x: gridPos.x,
          y: gridPos.y,
        },
      },
      PREVIEW_WIDGET_KEY
    );
  }

  if (!dashboardStore.hasPreviewWidget) return;

  const item = gridLayout.value.getItem(PREVIEW_WIDGET_KEY);
  if (!item) return;

  const newPos = calcNewPosition(item);

  if (mouseInGrid) {
    try {
      gridLayout.value.dragEvent(
        'dragstart',
        PREVIEW_WIDGET_KEY,
        newPos.x,
        newPos.y,
        selectedWidgetDimensions.value.h,
        selectedWidgetDimensions.value.w
      );
      previewVisible.value = true;
    } catch (error) {
      console.warn('Drag event error:', error);
    }
  } else {
    dashboardStore.removePreviewWidget();
  }
}, 50);

const onDragEnd = () => {
  if (!isComponentMounted.value) return;

  isDragging.value = false;
  if (!wrapper.value || !gridLayout.value) return;

  if (isMouseInGrid.value && previewVisible.value) {
    const previewItem = dashboardStore.activeDashboardTab?.dashboardTabWidgets?.find(
      (item) => item.dashboardTabWidgetGuid === PREVIEW_WIDGET_KEY
    );
    const previewComp = gridLayout.value.getItem(PREVIEW_WIDGET_KEY);
    if (!previewComp) return;

    const newPos = calcNewPosition(previewComp);

    if (previewItem && selectedWidget.value) {
      const newItemId = `${PENDING_WIDGET_PREFIX}-${uuid()}`;
      dashboardStore.addDashboardTabWidget(
        {
          ...selectedWidget.value,
          defaultJsonProperties: {
            ...selectedWidget.value.defaultJsonProperties,
            x: newPos.x,
            y: newPos.y,
          },
        },
        newItemId
      );
      dashboardStore.removePreviewWidget();
      selectedWidget.value = null;

      nextTick(() => {
        if (!isComponentMounted.value || !gridLayout.value) return;

        try {
          gridLayout.value.dragEvent(
            'dragend',
            newItemId,
            newPos.x,
            newPos.y,
            previewItem.defaultJsonProperties.w,
            previewItem.defaultJsonProperties.h
          );
          scrollToWidget(newItemId);
        } catch (error) {
          console.warn('Drag end event error:', error);
        }
      });
    }
  } else {
    // Clean up if drag ended outside grid
    dashboardStore.removePreviewWidget();
  }
};

// Grid snap calc
const calculateGridPosition = (clientX: number, clientY: number, itemWidth: number, itemHeight: number) => {
  if (!wrapper.value || !isComponentMounted.value) return { x: 0, y: 0 };
  const parentRect = wrapper.value.getBoundingClientRect();
  const relativeX = clientX - parentRect.left - DRAG_OFFSET_WIDTH;
  const relativeY = clientY - parentRect.top - DRAG_OFFSET_HEIGHT;

  const colPosition = Math.min(Math.floor(relativeX / colWidth.value), 12 - itemWidth);
  const rowPosition = Math.floor(relativeY / ROW_HEIGHT);
  return { x: colPosition, y: rowPosition };
};

const onLayoutUpdated = (newLayout: LayoutItem[]) => {
  if (!isComponentMounted.value) return;

  newLayout.forEach((layoutItem) => {
    dashboardStore.updateWidgetLayout(layoutItem.i, layoutItem);
  });
};

const emit = defineEmits(['add-new-dashboard-tab', 'open-settings-modal', 'open-help-modal']);
</script>

<template>
  <div class="tw-min-h-content tw-flex tw-bg-gradient-to-t tw-from-connx-grey-tint-95 tw-to-connx-grey-tint-95">
    <div
      v-if="activeLayout && isComponentMounted"
      class="tw-min-h-content tw-w-full"
      ref="wrapper"
      :class="{ GridRuler: editing }"
      :style="{ minHeight: containerHeight }"
    >
      <GridLayout
        :key="dashboardStore.activeDashboardTab?.guid"
        ref="gridLayout"
        @layout-updated="onLayoutUpdated"
        :layout="activeLayout"
        v-bind="gridSettings"
        v-if="isComponentMounted"
      >
        <GridItem
          v-for="item in activeLayout"
          @moved="onWidgetMoved"
          :key="item.i"
          v-bind="{ ...item, title: undefined }"
          class="tw-overflow-hidden vgl-editing tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center"
          :class="{
            'tw-opacity-50': item.i === PREVIEW_WIDGET_KEY,
            'tw-hidden': item.i === PREVIEW_WIDGET_KEY && !previewVisible,
            'vgl-editing': editing,
            'tw-z-1200': optionsOpen === item.i,
          }"
        >
          <connx-widget-wrapper
            v-if="item.i !== PREVIEW_WIDGET_KEY && isComponentMounted"
            @open-settings="emit('open-settings-modal', item.i)"
            @options-open="(open: boolean) => toggleOptionsOpen(item.i, open)"
            @open-help="emit('open-help-modal', item.i)"
            @remove-item="dashboardStore.removeWidget(item.i)"
            :settings-open="widgetSettingsId === item.i"
            :ref="(el: any) => setWidgetRef(el, item.i)"
            :cols="item.h"
            :rowHeight="ROW_HEIGHT"
            :key="item.i"
            :dashboard-widget-id="item.i"
            :editing="editing"
          >
          </connx-widget-wrapper>
        </GridItem>
      </GridLayout>
    </div>

    <div v-else-if="!loading && isComponentMounted" class="tw-flex tw-flex-col tw-max-w-184 tw-mx-auto tw-text-center">
      <div class="tw-my-auto tw-flex tw-flex-col tw-items-center tw-justify-center">
        <div class="tw-mx-auto tw-bg-connx-white tw-p-4 tw-rounded-lg tw-shadow-lg">
          <img class="tw-w-96" :src="dashboardIntroImage" />
        </div>
        <h3 class="tw-my-10">
          Click the Add button below to create your first dashboard, then follow the on-screen instructions.</h3
        >
        <div>
          <ConnxButton
            button-style="brand-primary"
            @click="emit('add-new-dashboard-tab')"
            class="tw-grow-0"
            title="Add New Dashboard"
            icon="fal fa-plus"
            icon-position="before"
          >
            Add a New Dashboard
          </ConnxButton>
        </div>
      </div>
    </div>

    <connx-dashboard-sidebar
      v-if="editing && dashboardStore.activeDashboardTab && isComponentMounted"
      @select-widget="setSelectedWidget"
      @widget-drag-start="onDragStart"
      @widget-drag="onDrag"
      @widget-drag-end="onDragEnd"
    />
  </div>
</template>

<style scoped>
.vgl-item {
  overflow: visible;
}

.vgl-layout {
  --vgl-placeholder-bg: var(--connx-primary);
  min-height: 100%;
}

.GridRuler {
  position: relative;
}

.GridRuler::before {
  position: absolute;
  width: 100%;
  height: 100%;
  margin: 0;
  content: '';
  background-image: linear-gradient(to right, var(--connx-grey-tint-80) 1px, transparent 1px),
    linear-gradient(to bottom, var(--connx-grey-tint-80) 1px, transparent 1px);
  background-repeat: repeat;
  background-size: calc(100% / 12) 115px;
  background-position: top left;
}
</style>
